import { Dialog } from "@ark-ui/solid";
import type { Component, ParentComponent, VoidComponent } from "solid-js";
import { mergeProps, Show } from "solid-js";
import { createPresence } from "@solid-primitives/presence";
import Portal from "../../utils/Portal";

type DrawerProps = {
  title: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  fullscreen?: boolean;
};

export const CLOSING_ANIMATION_DURATION_IN_MILLISECONDS = 300;

const Drawer: ParentComponent<DrawerProps> = (rawProps) => {
  const props = mergeProps({ fullscreen: false }, rawProps);

  const { isMounted } = createPresence(() => props.open, {
    transitionDuration: CLOSING_ANIMATION_DURATION_IN_MILLISECONDS,
  });

  return (
    <Dialog.Root
      id="drawer" // Used in `index.css`
      aria-label={props.title}
      open={props.open}
      onOpenChange={({ open }) => {
        props.setOpen(open);
      }}
      closeOnEscape={false}
      closeOnInteractOutside={true}
      onFocusOutside={(e) => {
        // Prevents drawer from closing when a child gains focus through another focus-trapped component (e.g. a Popover inside Subform Drawer)
        e.preventDefault();
      }}
      lazyMount
    >
      <Show when={isMounted()}>
        <Portal>
          <Dialog.Backdrop class="fixed inset-0 z-10 size-full animate-backdrop-fade-out bg-overlay-default open:animate-backdrop-fade-in" />
          <Dialog.Positioner
            class="fixed inset-0 z-10 shadow-elevation-light-4"
            classList={{ "pt-16": !props.fullscreen }}
          >
            <Dialog.Content class="h-full animate-drawer-close overflow-x-auto bg-surface-default open:animate-drawer-open">
              {props.children}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Show>
    </Dialog.Root>
  );
};

export const CloseTrigger: VoidComponent<{ asChild: Component }> = (props) => (
  <Dialog.CloseTrigger asChild={(parentProps) => <props.asChild {...parentProps()} />} />
);

export default Drawer;
